import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  AlertCircle,
  Brain,
  Scale,
  AlertTriangle,
  Volume2,
  FileWarning,
  FolderArchive,
  Download,
  Edit3,
  Mic,
  ArrowRight,
  Gavel,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Dashboard section: full educational content for the "Plan de Protección Preventiva".
 * Explains why and HOW to prepare the I-589 before ICE detention,
 * with explicit signing rules (Parts D and E) backed by USCIS instructions.
 */
export default function PreventiveProtectionPlan() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const detentionBalance = Number(user?.calls_detention_remaining || 0);
  const hasNoBalance = !user?.is_admin && detentionBalance <= 0;

  const handleFillFormCta = () => {
    if (hasNoBalance) {
      toast.error("Te quedaste sin llamadas de detención. Recarga aquí.");
      navigate("/precios?reason=no_balance_detention");
      return;
    }
    navigate("/scenario/phase2");
  };

  return (
    <section
      data-testid="dashboard-preventive-plan"
      className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5 mb-5"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-amber-950" strokeWidth={2.5} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-widest font-black text-amber-900">
            Plan de Protección Preventiva
          </p>
          <p className="text-xs text-amber-800">
            Su mejor escudo legal · prepárelo HOY, antes de cualquier enfrentamiento
          </p>
        </div>
      </div>

      <p className="text-sm text-zinc-800 leading-snug mb-4">
        La mejor defensa no se construye dentro del centro de detención. Se construye{" "}
        <span className="font-bold">desde su casa</span>, con tiempo y con su familia. Tener el I-589 ya
        preparado actúa como un escudo legal documentado por los manuales del Proyecto Florence y NIJC.
      </p>

      {/* ════════════════ CÓMO HACERLO PASO A PASO ════════════════ */}
      <h3 className="text-sm font-black text-zinc-900 mb-2 uppercase tracking-wider">
        Cómo prepararlo en 3 pasos
      </h3>
      <div className="space-y-3 mb-4">
        <Step
          n={1}
          Icon={Download}
          color="indigo"
          title="Descargue el formulario y el glosario"
          desc="Use la sección de abajo 'Información para que un familiar le ayude' donde encontrará los enlaces oficiales: el Formulario I-589 de USCIS y el glosario de miapoyo. Imprima ambos."
        />
        <Step
          n={2}
          Icon={Edit3}
          color="emerald"
          title="Llene el formulario con ayuda de nuestra IA"
          desc="Llame a nuestro asistente de voz desde aquí. La IA le va a guiar casilla por casilla en español, le va a dictar qué escribir en inglés y en MAYÚSCULAS, y le va a deletrear cada palabra. Nuestra IA ya tiene todo el glosario del formulario cargado."
        />
        <Step
          n={3}
          Icon={Gavel}
          color="rose"
          title="Guarde el formulario SIN firmar y SIN fecha"
          desc="Esta es la regla más importante. Las casillas de firma deben quedar en blanco. Solo se firman en el momento exacto que explicamos en la siguiente sección de abajo."
        />
      </div>

      {/* ════════════════ BOTÓN CTA · LLAMAR A LA IA AHORA ════════════════ */}
      <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-emerald-500 text-white rounded-2xl p-4 mb-5 shadow-lg">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Mic className="w-5 h-5" strokeWidth={2.5} />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-black opacity-90">
              ¿Listo para llenar el formulario?
            </p>
            <p className="text-sm font-bold leading-snug">
              Nuestra IA le ayuda paso a paso, casilla por casilla
            </p>
          </div>
        </div>
        <p className="text-xs opacity-90 mb-3 leading-snug">
          Tenga su formulario impreso y un bolígrafo a la mano. La IA le va a guiar en español, le va a dictar qué escribir en inglés y le va a deletrear cada palabra letra por letra.
        </p>
        <Button
          data-testid="preventive-cta-fill-form"
          onClick={handleFillFormCta}
          className="w-full h-12 sm:h-14 bg-white text-emerald-800 hover:bg-emerald-50 font-black text-base rounded-xl flex items-center justify-center gap-2 shadow-md"
        >
          <Mic className="w-5 h-5" />
          {hasNoBalance ? "Recargar llamadas para continuar" : "Hablar con la IA ahora para llenar mi I-589"}
          <ArrowRight className="w-5 h-5" />
        </Button>
        {hasNoBalance && (
          <p
            data-testid="preventive-no-balance-notice"
            className="text-xs text-amber-200 mt-2 leading-snug"
          >
            Tu plan tiene {detentionBalance} llamadas de detención restantes. Recarga para que la IA te guíe.
          </p>
        )}
      </div>

      {/* ════════════════ POR QUÉ HACERLO AHORA ════════════════ */}
      <h3 className="text-sm font-black text-zinc-900 mb-2 uppercase tracking-wider">
        Por qué hacerlo ahora
      </h3>
      <div className="space-y-3 mb-5">
        <Benefit
          n={1}
          Icon={AlertCircle}
          title="Evita la Deportación Expedita (Expedited Removal)"
          desc="Cuando ICE detiene a alguien, busca procesarlo lo más rápido posible. Si porta su Formulario I-589 pre-llenado en inglés y se lo muestra al oficial diciendo la frase clave, los agentes están obligados por ley a detener el proceso de expulsión inmediata. El documento actúa como evidencia física de que califica para una Entrevista de Temor Creíble (Credible Fear Interview)."
        />
        <Benefit
          n={2}
          Icon={Brain}
          title="Blindaje contra el pánico y el olvido"
          desc="Bajo el estrés extremo de un arresto, las personas suelen olvidar fechas clave, nombres de lugares o detalles específicos de su persecución. Si su familia tiene el formulario listo en casa, ya habrán redactado con calma el relato en inglés formal. Solo tendrán que imprimirlo, llevarlo al centro de detención y entregárselo para que usted lo firme."
        />
        <Benefit
          n={3}
          Icon={Scale}
          title="Acelera la solicitud de fianza (Bond)"
          desc="Para que un juez de inmigración otorgue una fianza y le deje salir en libertad, exige ver que su caso de asilo es real y serio. Presentar el I-589 en la primera semana de detención demuestra que su caso no es una táctica para ganar tiempo, lo cual aumenta drásticamente sus posibilidades de salir bajo fianza."
        />
      </div>

      {/* ════════════════ CUÁNDO SE FIRMA · INVESTIGACIÓN LEGAL ════════════════ */}
      <div
        data-testid="preventive-when-to-sign"
        className="bg-white border-2 border-blue-300 rounded-xl p-4 mb-5"
      >
        <div className="flex items-center gap-2 mb-3">
          <Gavel className="w-5 h-5 text-blue-700" strokeWidth={2.5} />
          <p className="text-sm font-black text-blue-900 uppercase tracking-wider">
            ¿Cuándo se firma el formulario?
          </p>
        </div>
        <p className="text-xs text-zinc-700 leading-snug mb-3 italic">
          Según las instrucciones oficiales del USCIS y los manuales del Proyecto Florence:
        </p>

        <div className="space-y-3">
          <SignaturePart
            label="Parte D"
            title="Firma del Solicitante (al presentar el formulario)"
            desc="Se firma el día en que usted ENTREGA el formulario en persona a un oficial de ICE, USCIS, o al Tribunal de Inmigración (EOIR). Es una declaración bajo juramento ante autoridad federal. NUNCA antes."
          />
          <SignaturePart
            label="Parte E"
            title="Firma en la Entrevista o Audiencia"
            desc="Se firma el día EXACTO de su entrevista con un oficial de asilo o de su audiencia ante un juez de inmigración. Se firma EN PERSONA, frente al oficial o juez."
          />
        </div>

        <p className="text-xs text-blue-900 bg-blue-50 border border-blue-200 rounded-lg p-3 mt-3 leading-snug">
          <span className="font-bold">Regla simple:</span> hasta que esté frente a un oficial de alto rango (oficial de asilo) o un juez de inmigración, ambas casillas de firma y todas las fechas de firma deben quedar{" "}
          <span className="font-black uppercase">en blanco</span>.
        </p>
      </div>

      {/* ════════════════ FRASE MÁGICA EN INGLÉS ════════════════ */}
      <div className="bg-zinc-900 text-white rounded-xl p-4 mb-5">
        <div className="flex items-center gap-2 mb-2">
          <Volume2 className="w-4 h-4 text-amber-400" />
          <p className="text-[10px] uppercase tracking-widest font-black text-amber-300">
            La frase que DEBE decir al mostrar el documento
          </p>
        </div>
        <p
          data-testid="preventive-magic-phrase-en"
          className="text-base sm:text-lg font-black tracking-tight text-white leading-snug select-all"
        >
          {"\u201CI have fear of returning to my country, this is my application.\u201D"}
        </p>
        <p className="text-xs text-zinc-400 mt-2 italic leading-snug">
          {"Traducción: \u201CTengo miedo de regresar a mi país, esta es mi solicitud.\u201D"}
        </p>
        <p className="text-xs text-zinc-300 mt-2 leading-snug">
          <span className="font-bold text-amber-300">Pronunciación:</span>{" "}
          {"\u201Cai jav fíar of returning tu mai contri, dis is mai aplikéishon\u201D."}
        </p>
      </div>

      {/* ════════════════ 3 REGLAS DE ORO ════════════════ */}
      <h3 className="text-sm font-black text-zinc-900 mb-2 uppercase tracking-wider">
        Reglas de Oro · OBLIGATORIAS
      </h3>
      <div
        data-testid="preventive-golden-rules"
        className="space-y-2.5 bg-white border-2 border-red-300 rounded-xl p-4"
      >
        <Rule
          Icon={FileWarning}
          title="NUNCA firmar ni poner fecha antes de tiempo"
          desc="Las casillas de firma deben quedar en blanco hasta que esté frente a un oficial de asilo o un juez de inmigración. Si firma meses antes, el gobierno puede acusarle de fraude o invalidar el documento bajo 8 U.S.C. § 1746 (false statements under penalty of perjury)."
        />
        <Rule
          Icon={FolderArchive}
          title="Mantenga copias seguras en TRES lugares"
          desc="Una copia en su mochila o billetera (en bolsa plástica para protegerla). Otra copia con su familia en casa. Una tercera copia digital en su correo electrónico. Si ICE confisca su papel, su familia puede imprimirlo de inmediato desde miapoyo."
        />
        <Rule
          Icon={Volume2}
          title="Decir la frase clave con firmeza"
          desc="Llevar el papel no es suficiente si se queda callado por miedo. Al mostrar el documento a ICE, diga con voz firme la frase mágica de arriba. Memorícela desde HOY."
        />
      </div>

      <p className="text-[10px] text-amber-900 mt-4 leading-snug">
        <AlertTriangle className="w-3 h-3 inline mr-1 -mt-0.5" />
        miapoyo NO es un despacho de abogados. Esto es información educativa de auto-ayuda autorizada por PBNDS 2011 §&nbsp;6.3 y la doctrina federal de auto-representación Pro Se. Reglas de firma basadas en las instrucciones oficiales del Formulario I-589 USCIS (rev. 2024) y los manuales del Florence Project y NIJC.
      </p>
    </section>
  );
}

const STEP_COLOR = {
  indigo: { bg: "bg-indigo-600", icon: "text-indigo-700" },
  emerald: { bg: "bg-emerald-600", icon: "text-emerald-700" },
  rose: { bg: "bg-rose-600", icon: "text-rose-700" },
};

function Step({ n, Icon, color, title, desc }) {
  const c = STEP_COLOR[color];
  return (
    <div className="bg-white border border-amber-200 rounded-xl p-3 flex gap-3">
      <div className="flex-shrink-0 flex flex-col items-center gap-1">
        <div className={`w-8 h-8 rounded-full ${c.bg} text-white flex items-center justify-center font-black text-sm`}>
          {n}
        </div>
        <Icon className={`w-4 h-4 ${c.icon}`} strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1 pt-0.5">
        <p className="text-sm font-black text-zinc-900 leading-snug">{title}</p>
        <p className="text-xs text-zinc-700 mt-0.5 leading-snug">{desc}</p>
      </div>
    </div>
  );
}

function Benefit({ n, Icon, title, desc }) {
  return (
    <div className="bg-white border border-amber-200 rounded-xl p-3 flex gap-3">
      <div className="flex-shrink-0 flex flex-col items-center gap-1">
        <div className="w-7 h-7 rounded-full bg-amber-600 text-white flex items-center justify-center font-black text-sm">
          {n}
        </div>
        <Icon className="w-4 h-4 text-amber-700" strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-black text-zinc-900 mb-1 leading-snug">{title}</p>
        <p className="text-xs text-zinc-700 leading-snug">{desc}</p>
      </div>
    </div>
  );
}

function SignaturePart({ label, title, desc }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0">
        <div className="inline-flex items-center px-2 py-1 rounded-md bg-blue-700 text-white text-[10px] font-black uppercase tracking-wider">
          {label}
        </div>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-bold text-zinc-900 leading-snug">{title}</p>
        <p className="text-xs text-zinc-700 mt-0.5 leading-snug">{desc}</p>
      </div>
    </div>
  );
}

function Rule({ Icon, title, desc }) {
  return (
    <div className="flex gap-3">
      <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-red-100 flex items-center justify-center">
        <Icon className="w-4 h-4 text-red-700" strokeWidth={2.5} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-black text-red-900 leading-snug">{title}</p>
        <p className="text-xs text-zinc-700 mt-0.5 leading-snug">{desc}</p>
      </div>
    </div>
  );
}
